import {useState, useCallback} from 'react'
import {useTranslation} from 'react-i18next'
import Card from '../../../components/ui/Card'
import TextInput from '../../../components/forms/TextInput'
import {useReservationContext} from '../../../context/ReservationProvider'
import {useMutation} from '@tanstack/react-query'
import {validateDiscountCode} from '../../../services/DiscountCodeService'

const ValidateDiscount = () => {
    const {t} = useTranslation()
    const {reservationState, setReservationState} = useReservationContext()

    const [code, setCode] = useState(reservationState?.discountCode || '')
    const [error, setError] = useState('')
    const [message, setMessage] = useState('')

    const {mutateAsync: validateAsync, isPending: isValidating} = useMutation({
        mutationFn: validateDiscountCode
    })

    const onValidate = useCallback(async () => {
        setError('')
        setMessage('')

        if (!code?.trim()) {
            setError(t('errors.discountCodeRequired', 'Discount code is required'))
            return
        }

        try {
            const result = await validateAsync(code.trim())

            // Persist the code in the reservation state on success
            setReservationState(prev => ({
                ...prev,
                discount_code_id: result?.id,
                discount: result
            }))

            const successMessage =
                (result && (result.message || result.msg)) || t('messages.discountApplied', 'Discount code applied')
            setMessage(successMessage)
        } catch (e) {
            const serverMessage =
                e?.errors?.code ||
                e?.errors?.message ||
                e?.message ||
                t('errors.discountCodeInvalid', 'Invalid discount code')
            setError(serverMessage)
        }
    }, [code, setReservationState, t, validateAsync])

    const onClear = useCallback(() => {
        setCode('')
        setError('')
        setMessage('')
        setReservationState(prev => {
            const next = {...prev}
            delete next.discount_code_id
            delete next.discount
            return next
        })
    }, [setReservationState])

    return (
        <Card
            title={t('cards.discount.title', 'Discount')}
            subtitle={t('cards.discount.subtitle', 'Have a discount code? Apply it here')}
        >
            <div className="flex flex-col gap-3">
                <div className="flex flex-col md:flex-row gap-3 items-end">
                    <div className="flex-1">
                        <TextInput
                            label={t('labels.discountCode', 'Discount code')}
                            placeholder={t('placeholders.discountCode', 'Enter your code')}
                            name="discountCode"
                            value={code}
                            onChange={(ev) => setCode(ev.target.value)}
                            errorMessage={error}
                            inputExtraClasses="border"
                        />
                    </div>

                    <div className="flex gap-2 items-end">
                        <button
                            type="button"
                            onClick={onValidate}
                            disabled={isValidating}
                            className={`${
                                isValidating ? 'opacity-60 cursor-not-allowed' : ''
                            } bg-primary hover:bg-primary/50 text-white font-bold p-2 rounded-lg h-10 md:h-auto`}
                        >
                            {isValidating ? t('actions.validating', 'Validating...') : t('actions.validate', 'Validate')}
                        </button>
                        {!!reservationState?.discount?.id ? (
                            <button
                                type="button"
                                onClick={onClear}
                                className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold p-2 rounded-lg h-10 md:h-auto"
                            >
                                {t('actions.clear', 'Clear')}
                            </button>
                        ) : null}
                    </div>
                </div>
                {!!message && (
                    <span className="text-sm text-green-600">{message}</span>
                )}
            </div>
        </Card>
    )
}

export default ValidateDiscount